import type { SalesQueueSlot, SalesQueueSlotsDay } from '../../model/slots-management/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useSlotsManagementStore } from '../../model/slots-management/store'

const nullableToInput = (value: number | string | null | undefined): number | string =>
  value === null || value === undefined ? '' : value

const inputToNullableInt = (value: number | string): number | null =>
  value === '' ? null : Number(value)

const normalizeDay = (day: SalesQueueSlotsDay): SalesQueueSlotsDay => ({
  ...day,
  slots: day.slots.map((slot): SalesQueueSlot => ({
    ...slot,
    agents_limit: nullableToInput(slot.agents_limit),
    completed_limit: nullableToInput(slot.completed_limit),
  })),
})

export const useSalesQueueSlotsApi = async (): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useSlotsManagementStore()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.sales-queue-slots-management.data')

  commonStore.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return false
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return false
    }

    const days: SalesQueueSlotsDay[] = (data.data?.days_of_week ?? []).map(normalizeDay)
    store.setSalesQueueDays(days)

    return true
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return false
  } finally {
    commonStore.setLoading(false)
  }
}

export const useSalesQueueSlotsSave = async (): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useSlotsManagementStore()

  const url = useAdminRoutes('admin.sales-queue-slots-management.update')

  if (!url) {
    toast.error('Unexpected error. Invalid request URL.')
    return false
  }

  const daysOfWeek = store.getSalesQueueDays.map(day => ({
    day_of_week: day.day_of_week,
    slots: day.slots.map(slot => ({
      time: slot.time,
      is_enabled: slot.is_enabled,
      agents_limit: inputToNullableInt(slot.agents_limit),
      completed_limit: inputToNullableInt(slot.completed_limit),
    })),
  }))

  store.setSaving(true)

  try {
    const { data } = await $axios.put<ApiResponse>(url, { schema: { days_of_week: daysOfWeek } })

    if (!data.success) {
      toast.error(data.message)
      return false
    }

    toast.success(data.message)

    return true
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return false
  } finally {
    store.setSaving(false)
  }
}
